import React, { useCallback, useEffect, useRef, useState } from 'react';

const navLinks = [
  { label: 'Features', href: '#features' },
  { label: 'The lookup', href: '#lookup' },
  { label: 'Screens', href: '#screens' },
  { label: 'Books', href: '#books' },
  { label: 'Languages', href: '#languages' },
  { label: 'Compare', href: '#compare' },
  { label: 'FAQ', href: '#faq' },
];

const sectionIds = navLinks.map((link) => link.href.slice(1));

/**
 * Flat dark masthead — a fixed band rather than a surface that fades in on
 * scroll, so the page always has one hard edge at the top.
 */
const SiteHeader: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setMenuOpen(false);
      toggleRef.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  // Highlight the section currently crossing the upper third of the viewport.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const elements = sectionIds
      .map((id) => document.getElementById(id))
      .filter((element): element is HTMLElement => element !== null);
    if (elements.length === 0) return undefined;

    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        });
        const firstVisible = sectionIds.find((id) => visible.has(id));
        setActiveId(firstVisible ?? null);
      },
      { rootMargin: '-20% 0px -70% 0px' },
    );

    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-night text-white">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-6 px-5 py-3 sm:px-8">
        <a
          href="#top"
          className="display-sm text-[1.375rem] text-white transition hover:text-primary-lift"
          onClick={closeMenu}
        >
          Lexeme
        </a>

        <nav aria-label="Primary" className="hidden items-center gap-7 lg:flex">
          {navLinks.map((link) => {
            const isActive = activeId === link.href.slice(1);
            return (
              <a
                key={link.href}
                href={link.href}
                aria-current={isActive ? 'true' : undefined}
                className={`border-b-2 pb-0.5 font-body text-[0.8125rem] font-medium tracking-wide transition ${
                  isActive
                    ? 'border-primary-lift text-white'
                    : 'border-transparent text-white/65 hover:text-white'
                }`}
              >
                {link.label}
              </a>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          {/* Where a signup button would go. The app is not released, so the
              masthead states that instead of asking for an address. */}
          <a
            href="#status"
            className="hidden border border-white/25 px-3 py-2 font-body text-[0.625rem] font-semibold uppercase tracking-small-caps text-white/70 transition hover:border-primary-lift hover:text-primary-lift sm:inline-flex"
          >
            Not released
          </a>
          <button
            ref={toggleRef}
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center border border-white/25 text-white lg:hidden"
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span aria-hidden="true">{menuOpen ? '✕' : '☰'}</span>
          </button>
        </div>
      </div>

      {menuOpen && (
        <nav
          id="mobile-nav"
          aria-label="Mobile"
          className="border-t border-white/10 bg-night-deep px-5 py-4 lg:hidden"
        >
          <ul>
            {navLinks.map((link) => (
              <li key={link.href} className="border-b border-white/10 last:border-b-0">
                <a
                  href={link.href}
                  className="block py-3.5 font-heading text-lg text-white/85 hover:text-primary-lift"
                  onClick={closeMenu}
                >
                  {link.label}
                </a>
              </li>
            ))}
            <li className="pt-4">
              <a
                href="#status"
                className="block border border-white/25 px-4 py-3 text-center font-body text-[0.6875rem] font-semibold uppercase tracking-small-caps text-white/70"
                onClick={closeMenu}
              >
                Not released
              </a>
            </li>
          </ul>
        </nav>
      )}
    </header>
  );
};

export default SiteHeader;
